import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, Clock, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { CONTACT, SERVICES, PLANS, planLabel } from "../../content/site";
import { whatsappUrl, WhatsAppIcon } from "./WhatsApp";
import { AvailableBadge } from "./AvailableBadge";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./motion";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", contact: "", service: "", message: "" };
const field = "w-full rounded-2xl border border-[#E2DDD0] bg-[#FBF9F4] px-5 py-4 text-[#1B281E] placeholder:text-[#728375] outline-none transition-[border-color,box-shadow] focus:border-[#1E5631] focus:ring-4 focus:ring-[#1E5631]/10";

const Info = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-4 border-b border-[#FBF9F4]/15 py-5">
    <Icon className="mt-1 h-5 w-5 shrink-0 text-[#DCEFBE]" />
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#FBF9F4]/70">{label}</p>
      <div data-testid={testId} className="mt-1 text-lg text-[#FBF9F4]">{children}</div>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onPick = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("gw:service", onPick);
    return () => window.removeEventListener("gw:service", onPick);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) {
      toast.error(`Bitte schreiben Sie uns direkt an ${CONTACT.email}.`);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Danke! Ihre Anfrage ist angekommen.");
    } catch {
      toast.error(`Das hat leider nicht geklappt. Bitte versuchen Sie es erneut oder schreiben Sie an ${CONTACT.email}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="bg-[#FBF9F4] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Kontakt" lines={["Erzählen Sie uns", "von Ihrem Garten."]} intro="Ein paar Zeilen genügen. Wir melden uns bei Ihnen und besprechen alles Weitere." />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="rounded-[28px] border border-[#E2DDD0] bg-white p-6 sm:p-10 lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-start justify-center">
                <CheckCircle2 className="h-12 w-12 text-[#1E5631]" />
                <h3 className="mt-6 font-display text-4xl text-[#1B281E]">Vielen Dank!</h3>
                <p className="mt-3 max-w-md text-[#4B5B4E]">Ihre Anfrage ist bei uns angekommen. Wir melden uns so bald wie möglich bei Ihnen.</p>
                <button data-testid="contact-new-request" onClick={() => setDone(false)} className="link-underline mt-8 font-medium text-[#1E5631]">Weitere Anfrage senden</button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-medium text-[#1B281E]">Name
                    <input data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} className={field} placeholder="Ihr Name" />
                  </label>
                  <label className="grid gap-2 text-sm font-medium text-[#1B281E]">E-Mail oder Telefon
                    <input data-testid="contact-input-contact" required minLength={3} value={form.contact} onChange={set("contact")} className={field} placeholder="Wie erreichen wir Sie?" />
                  </label>
                </div>
                <label className="grid gap-2 text-sm font-medium text-[#1B281E]">Gewünschte Leistung
                  <select data-testid="contact-select-service" required value={form.service} onChange={set("service")} className={`${field} appearance-none`}>
                    <option value="" disabled>Bitte auswählen</option>
                    {SERVICES.map((s) => <option key={s.key} value={s.title}>{s.title}</option>)}
                    {PLANS.map((p) => <option key={p.key} value={planLabel(p)}>{planLabel(p)}</option>)}
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </label>
                <label className="grid gap-2 text-sm font-medium text-[#1B281E]">Nachricht
                  <textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={set("message")} className={`${field} resize-none`} placeholder="Was soll gemacht werden? Wie groß ist der Garten ungefähr?" />
                </label>
                <button data-testid="contact-form-submit-button" type="submit" disabled={loading} className="btn-primary mt-2 justify-center py-4 text-base disabled:opacity-70">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Anfrage senden <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p data-testid="contact-privacy-note" className="text-xs leading-relaxed text-[#4B5B4E]">
                  Mit dem Absenden werden Ihre Angaben zur Bearbeitung der Anfrage verwendet. Mehr dazu in der{" "}
                  <Link to="/datenschutz" data-testid="contact-privacy-link" className="font-medium text-[#1E5631] underline underline-offset-2">Datenschutzerklärung</Link>.
                </p>
              </form>
            )}
          </Reveal>
          <Reveal delay={0.12} className="relative overflow-hidden rounded-[28px] bg-[#1E5631] p-6 sm:p-10 lg:col-span-5">
            <div className="grain absolute inset-0" />
            <div className="relative">
              <h3 className="font-display text-3xl text-[#FBF9F4]">Direkter Kontakt</h3>
              <div className="mt-6">
                <Info icon={Mail} label="E-Mail" testId="contact-email">
                  <a href={`mailto:${CONTACT.email}`} className="link-underline break-all">{CONTACT.email}</a>
                </Info>
                <Info icon={Phone} label="Telefon" testId="contact-phone">
                  <a href={`tel:${CONTACT.phone}`} className="link-underline">{CONTACT.phone}</a>
                </Info>
                <Info icon={WhatsAppIcon} label="WhatsApp – gern mit Fotos vom Garten" testId="contact-whatsapp">
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="link-underline">Nachricht an Jonas</a>
                </Info>
                <Info icon={Clock} label="Zeiten für Termine" testId="contact-hours"><span className="whitespace-pre-line">{CONTACT.hours}</span><span className="block"><AvailableBadge /></span></Info>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
